<template>
    <section class="assign-package-panel tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-sm tw-overflow-hidden xl:tw-col-span-7">
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-4 tw-pt-4 tw-mb-3">
            <div class="tw-text-xs tw-font-bold tw-uppercase tw-text-gray-700">
                {{ $t("assignDriver.searchAvailablePackages") }}
                <span class="tw-text-xs tw-font-normal tw-normal-case tw-text-gray-400">
                    ({{ $t("assignDriver.filterToAssign") }})
                </span>
            </div>
            <button
                class="tw-text-xs tw-font-bold tw-text-blue-600"
                type="button"
                @click.prevent="$emit('clear-filters')"
            >
                {{ $t("assignDriver.resetFilters") }}
            </button>
        </div>

        <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2 tw-px-4 tw-mb-3">
            <div>
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-1">
                    {{ $t("assignDriver.bookingNumber") }}
                </label>
                <a-input
                    :value="searchBox"
                    class="tw-w-full"
                    :placeholder="$t('assignDriver.bookingPlaceholder')"
                    @input="$emit('update-search-box', $event.target ? $event.target.value : $event)"
                    @pressEnter="$emit('search-packages')"
                >
                    <a-icon slot="prefix" type="search" />
                    <span slot="suffix" class="assign-input-enter">Enter</span>
                </a-input>
            </div>

            <div>
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-1">
                    {{ $t("assignDriver.zone") }}
                </label>
                <a-select
                    :value="zoneId"
                    show-search
                    allow-clear
                    class="tw-w-full"
                    :default-active-first-option="false"
                    :filter-option="false"
                    :not-found-content="null"
                    :placeholder="$t('assignDriver.selectZone')"
                    @search="$emit('search-zone', $event)"
                    @change="$emit('change-zone', $event)"
                >
                    <a-select-option
                        v-for="zone in zoneList"
                        :key="zone.zone_id"
                        :value="zone.zone_id"
                    >
                        {{ zone.zone_name }}
                    </a-select-option>
                </a-select>
            </div>

            <div class="md:tw-col-span-2">
                <label class="tw-block tw-text-xs tw-font-bold tw-text-gray-600 tw-mb-1">
                    {{ $t("assignDriver.receiverPhone") }}
                </label>
                <a-input
                    ref="receiverPhoneInput"
                    :value="receiverPhone"
                    class="tw-w-full"
                    :placeholder="$t('assignDriver.searchPhoneNumber')"
                    @input="$emit('update-receiver-phone', $event.target ? $event.target.value : $event)"
                    @pressEnter="$emit('search-receiver-phone')"
                >
                    <a-icon slot="prefix" type="phone" />
                    <span slot="suffix" class="assign-input-enter">Enter</span>
                </a-input>
            </div>
        </div>

        <div class="available-package-scroll tw-space-y-2 tw-overflow-y-auto tw-px-3 tw-pb-3">
            <button
                v-for="(record, index) in rows"
                :key="record.booking_id"
                ref="packageRows"
                class="assign-package-row tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-py-2 tw-text-left hover:tw-border-blue-200 hover:tw-bg-blue-50 focus:tw-border-blue-300 focus:tw-bg-blue-50 disabled:tw-opacity-60"
                :class="{ 'tw-border-blue-300 tw-bg-blue-50': activeIndex === index }"
                type="button"
                :disabled="isSelected(record)"
                @click.prevent="$emit('add', record)"
                @keydown.enter.prevent="$emit('add', record)"
                @focus="activeIndex = index"
            >
                <span class="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-50 tw-text-blue-500">
                    <i class="fa fa-plus"></i>
                </span>
                <span class="tw-grid tw-min-w-0 tw-flex-1 tw-grid-cols-12 tw-items-center tw-gap-3">
                    <span class="tw-col-span-12 md:tw-col-span-7 tw-min-w-0">
                        <span class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                            <span class="tw-text-xs tw-font-bold tw-text-blue-600">{{ record.booking_number }}</span>
                            <span :class="packageTypeClass(record)">{{ record.package_type_kh || record.package_type || '-' }}</span>
                            <span class="assign-info-chip">{{ record.number_of_package || 0 }} {{ $t("assignDriver.pkg") }}</span>
                            <span class="assign-info-chip tw-text-gray-500">{{ formatAmount(record.purchase_amount) }}</span>
                        </span>
                        <span class="tw-mt-1 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-text-xs">
                            <span class="tw-font-normal tw-text-gray-400 tw-truncate">{{ record.shop_name || '-' }}</span>
                            <span class="tw-text-gray-300">•</span>
                            <span class="tw-flex-none tw-font-normal tw-text-gray-500">{{ formatDate(record.booking_time) }}</span>
                        </span>
                    </span>
                    <span class="tw-col-span-12 md:tw-col-span-5 tw-min-w-0 tw-text-left md:tw-text-right">
                        <span class="tw-block tw-text-xs tw-font-bold tw-text-gray-900 tw-whitespace-nowrap">{{ record.receiver_phone || '-' }}</span>
                        <span class="tw-mt-1 tw-block tw-text-xs tw-text-gray-400 tw-truncate">{{ record.destination || '-' }}</span>
                    </span>
                </span>
            </button>

            <div v-if="!rows.length" class="tw-flex tw-h-56 tw-flex-col tw-items-center tw-justify-center tw-py-10 tw-text-center">
                <div class="tw-relative tw-mb-4 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50 tw-text-blue-600">
                    <i class="fa fa-cube tw-text-2xl"></i>
                    <span class="tw-absolute tw--right-1 tw--bottom-1 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-blue-100 tw-bg-white tw-text-blue-500">
                        <i class="fa fa-search tw-text-xs"></i>
                    </span>
                </div>
                <div class="tw-text-sm tw-font-bold tw-text-gray-900">
                    {{ $t("assignDriver.noAvailablePackages") }}
                </div>
                <div class="tw-mt-2 tw-max-w-md tw-text-xs tw-leading-5 tw-text-gray-500">
                    {{ $t("assignDriver.noAvailablePackagesHint") }}
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import moment from "moment";

export default {
    name: "AvailablePackagesPanel",
    data() {
        return {
            activeIndex: 0
        };
    },
    props: {
        rows: {
            type: Array,
            default: () => []
        },
        selectedIds: {
            type: Array,
            default: () => []
        },
        searchBox: {
            default: null
        },
        receiverPhone: {
            default: null
        },
        zoneId: {
            default: null
        },
        zoneList: {
            type: Array,
            default: () => []
        }
    },
    methods: {
        getReceiverPhoneElement() {
            const input = this.$refs.receiverPhoneInput;

            if (!input) {
                return null;
            }

            if (input.$el && input.$el.querySelector) {
                return input.$el.querySelector("input");
            }

            return input;
        },
        focusReceiverPhoneInput(selectText = false) {
            this.$nextTick(() => {
                const input = this.getReceiverPhoneElement();

                if (!input) {
                    return;
                }

                input.focus();

                if (selectText && input.select) {
                    input.select();
                }
            });
        },
        focusFirstPackageRow() {
            this.$nextTick(() => {
                const rows = this.$refs.packageRows;
                const firstRow = Array.isArray(rows) ? rows[0] : rows;

                this.activeIndex = 0;

                if (firstRow && firstRow.focus) {
                    firstRow.focus();
                }
            });
        },
        isSelected(record) {
            return this.selectedIds.some(id => id == record.booking_id);
        },
        formatAmount(value) {
            const amount = Number(value || 0);

            return "$ " + amount.toFixed(2);
        },
        formatDate(value) {
            if (!value) {
                return "-";
            }

            const date = moment(value);

            return date.isValid() ? date.format("DD-MM-YYYY") : value;
        },
        packageTypeClass(record) {
            const type = String(record.package_type_kh || record.package_type || "").toLowerCase();
            const base = "tw-flex-none tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs tw-font-normal";

            if (type.indexOf("express") !== -1) {
                return base + " tw-border-blue-200 tw-bg-blue-50 tw-text-blue-600";
            }

            if (type.indexOf("fragile") !== -1) {
                return base + " tw-border-red-200 tw-bg-red-50 tw-text-red-500";
            }

            if (type.indexOf("heavy") !== -1) {
                return base + " tw-border-indigo-200 tw-bg-indigo-50 tw-text-indigo-600";
            }

            if (type.indexOf("standard") !== -1) {
                return base + " tw-border-gray-200 tw-bg-gray-50 tw-text-gray-700";
            }

            return base + " tw-border-yellow-200 tw-bg-yellow-50 tw-text-yellow-700";
        }
    },
    watch: {
        rows() {
            this.activeIndex = 0;
        }
    }
};
</script>
